import clsx from 'clsx';
import { FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from 'src/components/0100_button';
import Input from 'src/components/0100_input';
import Textarea from 'src/components/0100_textarea';
import { createMod, updateMod } from 'src/graphql/mutations/events.graphql';
import {
  ICreateModMutation,
  ICreateModMutationVariables,
  IUpdateModMutation,
  IUpdateModMutationVariables,
} from 'src/graphql/mutations/events.graphql.types';
import useButtonStates from 'src/hooks/buttonStates/useButtonStates';
import { useMutation } from 'urql';
import ModSignups, { TModSignup } from './ModSignups';

export type TMod = {
  id?: number;
  canManageMod?: boolean;
  name?: string;
  timeframe?: string | null;
  participationLimit?: number;
  description?: string | null;
  modSignups?: TModSignup[];
};

interface IProps extends TMod {
  modSectionId?: number;
  eventAllowsMoreSignup?: boolean;
  waitlistAllowed?: boolean;
  sectionAllowsMoreSignup?: boolean;
  eventHasEnded?: boolean;
  modSignupIsOpen?: boolean;
}

const Mod: FC<IProps> = ({
  id,
  canManageMod,
  modSectionId,
  eventAllowsMoreSignup = false,
  waitlistAllowed = false,
  sectionAllowsMoreSignup = false,
  eventHasEnded = false,
  modSignupIsOpen = false,
  ...props
}) => {
  const {
    register,
    reset,
    watch,
    setValue,
    formState: { isDirty, isValid },
  } = useForm({
    defaultValues: {
      name: props.name ?? '',
      limit: props.participationLimit,
      timeframe: props.timeframe,
      description: props.description,
    },
  });
  const { name, limit, description, timeframe } = watch();
  const { buttonState } = useButtonStates();
  const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false);
  const [createResult, create] = useMutation<
    ICreateModMutation,
    ICreateModMutationVariables
  >(createMod);
  const [_updateResult, update] = useMutation<
    IUpdateModMutation,
    IUpdateModMutationVariables
  >(updateMod);

  const handleCreate = useCallback(() => {
    if (id) return;
    if (!modSectionId) return;

    create({
      modSectionId,
      name,
    }).then(res => {
      if (res.data?.createMod?.modSection?.id) {
        setValue('name', '');
      }
    });
  }, [id, modSectionId, name, create, setValue]);

  const handleUpdate = useCallback(() => {
    if (!id) return;
    if (!isDirty) return;

    update({
      modId: id,
      name,
      participationLimit: Number(limit),
      description,
      timeframe,
    }).then(res => {
      if (res.data?.updateMod?.mod?.id) {
        setIsUpdateSuccessful(true);
        setTimeout(() => setIsUpdateSuccessful(false), 3000);
      }
    });
  }, [update, id, isDirty, name, limit, description, timeframe]);

  useEffect(() => {
    reset({
      name: props.name,
      limit: props.participationLimit,
      description: props.description,
      timeframe: props.timeframe,
    });
  }, [
    reset,
    props.name,
    props.participationLimit,
    props.description,
    props.timeframe,
  ]);

  useEffect(() => {
    if (isDirty) {
      setIsUpdateSuccessful(false);
    }
  }, [isDirty]);

  if (!canManageMod && !props.participationLimit) return null;

  return (
    <div
      className={clsx(
        'border rounded border-juno-gray-700 hover:border-juno-gray-200 transition-all duration-300 grid gap-2 place-items-center self-start',
        isUpdateSuccessful ? 'bg-lime-600/75' : 'gray-box',
      )}
    >
      <div
        className={clsx(
          'flex justify-center gap-2 transition-all duration-300 ease-in-out',
          id && 'w-full p-4',
        )}
      >
        {canManageMod ? (
          <div className="w-full">
            {id && (
              <div className="text-sm uppercase text-juno-gray-200">
                Mod Name
              </div>
            )}
            <div className={clsx('grid gap-2', !id && 'py-4')}>
              {!id && <div className="text-center text-xl">New Mod</div>}
              <Input
                fullWidth
                placeholder="Mod Name"
                {...register('name', { required: true, onBlur: handleUpdate })}
              />
              {!id && (
                <div className="flex justify-center">
                  <Button
                    defaultLabel="Add Mod"
                    state={buttonState({
                      isHighlight: true,
                      isValid,
                      isDirty: true,
                      isFetching: createResult.fetching,
                    })}
                    onClick={handleCreate}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-left w-full text-xl text-shadow">
            {props.name}
          </div>
        )}
        {id && canManageMod && (
          <div className="min-w-[64px] w-[64px]">
            <div className="text-right text-sm uppercase text-juno-gray-200">
              Limit
            </div>
            <Input
              type="number"
              className="text-right"
              fullWidth
              {...register('limit', { onBlur: handleUpdate })}
              onFocus={x => x.target.select()}
            />
          </div>
        )}
        {id && !canManageMod && (
          <div className="whitespace-nowrap">
            {props.participationLimit} Limit
          </div>
        )}
      </div>
      {id && (
        <div className="grid gap-2 p-4 pt-0 w-full">
          <div>
            <div className="text-sm uppercase text-juno-gray-200">
              Timeframe
            </div>
            {canManageMod ? (
              <Input
                fullWidth
                placeholder="Ex: Sun 3PM - 5PM"
                {...register('timeframe', { onBlur: handleUpdate })}
              />
            ) : (
              <div className="text-juno-gray-200">
                {props.timeframe ?? 'Unspecified'}
              </div>
            )}
          </div>
          {(canManageMod || props.description) && (
            <div>
              <div className="text-sm uppercase text-juno-gray-200">
                Description
              </div>
              {canManageMod ? (
                <Textarea
                  width="w-full"
                  height="h-24"
                  {...register('description', { onBlur: handleUpdate })}
                />
              ) : (
                <div>{props.description}</div>
              )}
            </div>
          )}
        </div>
      )}
      {id && (
        <ModSignups
          id={id}
          participationLimit={props.participationLimit}
          modSignups={props.modSignups}
          canManageMod={canManageMod}
          eventAllowsMoreSignup={eventAllowsMoreSignup}
          sectionAllowsMoreSignup={sectionAllowsMoreSignup}
          waitlistAllowed={waitlistAllowed}
          eventHasEnded={eventHasEnded}
          modSignupIsOpen={modSignupIsOpen}
        />
      )}
    </div>
  );
};

export default Mod;
